<template>
  <v-navigation-drawer
    :model-value="modelValue"
    temporary
    :width="280"
    class="ws-nav"
    :aria-label="t('layout.nav_main')"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <nav class="ws-nav__inner">
      <div class="ws-nav__sections">
        <div v-for="section in sections" :key="section.id" class="ws-nav__section">
          <p v-if="section.labelKey" class="ws-nav__section-title">
            {{ t(section.labelKey) }}
          </p>

          <RouterLink
            v-for="item in section.items"
            :key="item.name"
            :to="item.to"
            class="ws-nav__item"
            :class="{ 'ws-nav__item--active': isActive(item) }"
            :aria-current="isActive(item) ? 'page' : undefined"
            @click="onNavigate"
          >
            <v-icon :icon="item.icon" size="22" class="ws-nav__icon" aria-hidden="true" />
            <span class="ws-nav__label">{{ t(item.labelKey) }}</span>
          </RouterLink>
        </div>
      </div>
</nav>
  </v-navigation-drawer>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useNavigation } from '@/composables/useNavigation'

/**
 * Navegación principal: un drawer que se abre desde la cabecera y se cierra al
 * navegar. Se probó con un rail fijo en escritorio y molestaba tenerlo siempre
 * delante, así que el menú vuelve a estar oculto; lo que se conserva de aquello
 * son las secciones.
 *
 * Los ítems salen de meta.nav de las rutas (composables/useNavigation.js).
 */
defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()
const route = useRoute()
const { sections } = useNavigation()

function isActive(item) {
  return route.name === item.name
}

function onNavigate() {
  emit('update:modelValue', false)
}
</script>

<style scoped>
.ws-nav {
  background: var(--ws-surface-0);
  border-right: 1px solid var(--ws-border-subtle);
}

.ws-nav__inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--ws-space-3) var(--ws-space-2);
}

.ws-nav__sections {
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--ws-space-4);
}

.ws-nav__section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ws-nav__section-title {
  margin: 0 0 var(--ws-space-1);
  padding: 0 var(--ws-space-3);
  font-size: var(--ws-text-xs);
  font-weight: var(--ws-fw-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ws-text-low);
}

.ws-nav__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--ws-space-3);
  min-height: 44px;
  padding: 0 var(--ws-space-3);
  border-radius: var(--ws-radius-md);
  color: var(--ws-text-high);
  text-decoration: none;
  transition: background-color var(--ws-duration) var(--ws-ease), color var(--ws-duration) var(--ws-ease);
}

.ws-nav__item:hover {
  background-color: var(--ws-glass-4);
}

/* Activo: barra de marca a la izquierda en vez de pintar el ítem entero.
   Es más legible en el rail y no compite con el contenido. */
.ws-nav__item--active {
  background-color: var(--ws-glass-3);
  color: #fff;
}

.ws-nav__item--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  border-radius: 0 var(--ws-radius-xs) var(--ws-radius-xs) 0;
  background: var(--ws-gradient-brand);
}

.ws-nav__item--active .ws-nav__icon {
  color: var(--ws-brand-pink-400);
}

.ws-nav__label {
  font-size: var(--ws-text-base);
  font-weight: var(--ws-fw-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>
